import React from 'react';
import styled from 'styled-components/native';
import {
  NativeSyntheticEvent,
  TextInputSubmitEditingEventData,
  TouchableOpacity,
  View,
} from 'react-native';
import TextGeneric from '../textGeneric';
import Icon from '../icon';
import {Theme} from '../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import MenuIcon from './menuIcon';
import BackIcon from './backIcon';
import {hdp, wdp} from '../../utils/responsive';
import {HeaderProps} from '../../interfaces/components/header';
import {useTheme} from 'styled-components';
import GradientBackground from './gradientBackground';
import SearchBar from '../searchBar';
import {useCustomHeaderHeight} from '../../utils/hooks/useHeaderHeight';

export const HeaderContainer = styled.View<{
  headerHeight: number;
  topInset?: number;
}>`
  height: ${({headerHeight, topInset}) =>
    headerHeight - Math.max(topInset || 0, hdp(2))}px;
  justify-content: flex-end;
  margin-top: ${({topInset}) => Math.max(topInset || 0, hdp(2))}px;
`;
export const HeaderContentContainer = styled.View<{marginTop: number}>`
  flex-direction: row;
  width: 100%;
  padding-horizontal: 2%;
  align-self: center;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-color: ${({theme}) => theme.header.background};
`;
export const HeaderTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.header.primary};
  max-width: ${({theme}) => wdp(90) - theme.text.s3 * 3}px;
  text-align: center;
`;
export const HeaderBackground = styled(GradientBackground)<{
  headerHeight: number;
  topInset: number;
}>`
  height: ${({headerHeight, topInset}) =>
    headerHeight - Math.max(topInset, hdp(2))}px;
  position: absolute;
  width: 100%;
`;
export const HeaderItemsContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;
export const ExtraPaddingHeader = styled.View<{headerHeight?: number}>`
  width: 100%;
  height: ${({headerHeight}) => headerHeight}px;
`;
const HeaderSearchBar = styled(SearchBar)<{isMinWidth: boolean}>`
  width: ${({theme, isMinWidth}) =>
    wdp(90) - theme.text.s3 * (isMinWidth ? 3 : 1.5)}px;
  height: 80%;
  align-self: center;
  background-color: ${({theme}) => theme.homeBackground};
`;

/**
 * Header Component
 * Renders a custom navigation header with optional menu and back icons, a title, and notification functionality.
 *
 * @param {HeaderProps} props - Component props
 * @param {string} props.title - Title to display in the header.
 * @param {boolean} props.menuHeader - Determines if a menu icon should be shown. Defaults to false.
 * @param {boolean} props.withSeparator - If true, adds a separator line at the bottom of the header.
 * @param {boolean} props.backHeader - Determines if a back icon should be shown. Defaults to false.
 * @param {object} props.navigation - Navigation object provided by react-navigation.
 * @param {boolean} props.hideNotification - If true, hides the notification icon and functionality.
 *
 * @returns {React.ReactElement} - Rendered header component.
 */
const Header = ({
  title = '',
  menuHeader,
  backHeader,
  isDarkMode,
  rightIcon,
  leftIcon,
  showLeftLogo,
  isSearch,
  setSearch,
  search,
  onSearchPress,
  onGoBackPress,
}: HeaderProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {top: topInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();

  return (
    <HeaderContainer topInset={topInset} headerHeight={headerHeight}>
      <HeaderContentContainer marginTop={topInset}>
        <HeaderItemsContainer>
          {leftIcon ? (
            leftIcon()
          ) : backHeader ? (
            <BackIcon
              onPress={onGoBackPress}
              tintColor={theme.header.primary}
              canGoBack
            />
          ) : showLeftLogo ? (
            <Icon
              name={'logo'}
              color={theme.header.primary}
              size={theme.text.s3}
            />
          ) : isSearch ? (
            <View style={{width: theme.text.s3, height: theme.text.s3}} />
          ) : (
            <View pointerEvents={'none'} style={{opacity: 0}}>
              <BackIcon tintColor={theme.header.primary} canGoBack />
            </View>
          )}
        </HeaderItemsContainer>
        <HeaderItemsContainer>
          {title ? (
            <HeaderTitle isDarkTheme={isDarkMode} numberOfLines={1} bold>
              {typeof title === 'function' ? title() : title || ''}
            </HeaderTitle>
          ) : isSearch ? (
            <TouchableOpacity disabled={!onSearchPress} onPress={onSearchPress}>
              <HeaderSearchBar
                //@ts-ignore
                disabled={!onSearchPress}
                isMinWidth={
                  (!!leftIcon || !!backHeader || !!showLeftLogo) &&
                  (!!rightIcon || !!menuHeader)
                }
                pointerEvents={onSearchPress ? 'none' : 'auto'}
                value={search || ''}
                textInputStyle={{
                  padding: 0,
                  paddingHorizontal: theme.text.s10,
                }}
                onChangeText={(text: string) => setSearch?.(text)}
                onSubmitEditing={(
                  e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
                ) => setSearch?.(e.nativeEvent.text)}
              />
            </TouchableOpacity>
          ) : (
            <Icon
              name={'genio'}
              color={theme.header.primary}
              width={theme.text.s3 * 3.75}
              height={theme.text.s3}
            />
          )}
        </HeaderItemsContainer>
        <HeaderItemsContainer>
          {rightIcon ? (
            typeof rightIcon === 'function' ? (
              rightIcon()
            ) : (
              rightIcon
            )
          ) : menuHeader ? (
            <MenuIcon tintColor={theme.header.primary} canGoBack />
          ) : isSearch ? (
            <View style={{width: theme.text.s3, height: theme.text.s3}} />
          ) : (
            <View pointerEvents={'none'} style={{opacity: 0}}>
              <BackIcon tintColor={theme.header.primary} canGoBack />
            </View>
          )}
        </HeaderItemsContainer>
      </HeaderContentContainer>
    </HeaderContainer>
  );
};

export default Header;
